import React, { useState, useEffect, Suspense, lazy } from 'react';
import SeoHead from '@/components/SeoHead';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, BookOpen, Columns, Eye, LifeBuoy } from 'lucide-react';
import Footer from '@/components/Footer';
import SiteHeader from '@/components/SiteHeader';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';
import TiersSection from '@/components/landing/TiersSection';
import { track } from '@/utils/analytics';

import RequestCatalogSection from '@/components/home/RequestCatalogSection';

const Index = () => {
  const navigate = useNavigate();
  const { user, authReady } = useAuth();
  const { isFeatureEnabled, ensureLoaded: ensureFlagsLoaded, resolved: flagsResolved } = useFeatureFlags();
  // Ask for the flags immediately so the recommender card can be decided once
  // rather than shown optimistically and then pulled away.
  useEffect(() => { ensureFlagsLoaded(); }, [ensureFlagsLoaded]);
  const isSignedIn = authReady && !!user;

  useEffect(() => { track('home_viewed'); }, []);

  const handleStartConfiguration = () => {
    navigate(isSignedIn ? '/projects' : '/auth?tab=signup');
  };

  return (
    <div className="relative min-h-dvh flex flex-col">
      <SeoHead route="/" />
      <SiteHeader />

      <main>
      {/* Hero Section with improved readability */}
      <section className="relative z-20 flex items-center justify-center min-h-[70vh] sm:min-h-[80vh] text-center px-4 sm:px-6 py-8">
        {/* Dark overlay for better text contrast */}
        <div className="absolute inset-0 bg-black/10 rounded-xl"></div>
        
        <div className="relative z-10 max-w-4xl">
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight sm:leading-relaxed drop-shadow-lg">
            Professional LED
            <span className="block bg-gradient-to-r from-brand-blue to-brand-magenta bg-clip-text text-transparent pb-2">
              Panel Configurator
            </span>
          </h1>
          <p className="text-base sm:text-xl text-white mb-6 sm:mb-8 max-w-2xl mx-auto drop-shadow-md px-2">
            Design, configure, and optimise LED panel installations with precision and ease. 
            Professional tools for professional results.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" style={{ visibility: authReady ? 'visible' : 'hidden' }}>
            {isSignedIn ? (
              <Button
                size="lg"
                onClick={handleStartConfiguration}
                className="bg-gradient-to-r from-brand-blue to-brand-magenta hover:from-brand-blue-dark hover:to-brand-blue text-white px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Start Configuring
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
            ) : (
              <>
                <Button
                  size="lg"
                  onClick={() => navigate('/auth?tab=signup')}
                  className="bg-gradient-to-r from-brand-blue to-brand-magenta hover:from-brand-blue-dark hover:to-brand-blue text-white px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Create free account
                  <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/auth')}
                  className="bg-white/5 text-white border-white/30 hover:bg-white/10 px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg"
                >
                  Sign in
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              onClick={() => navigate('/help')}
              className="text-white/70 hover:text-white text-sm"
            >
              How it works →
            </Button>
          </div>
        </div>
      </section>

      {/* Signed-out: tier picker. Signed-in: 5-step guide */}
      {isSignedIn ? (
        <section className="relative z-20 px-4 sm:px-6 -mt-4" aria-labelledby="getting-started-heading">
          <h2 id="getting-started-heading" className="sr-only">Getting started</h2>
          <div className="container mx-auto max-w-2xl">
            <div
              onClick={() => navigate('/help')}
              className="flex items-center gap-4 p-5 sm:p-6 rounded-xl bg-white/5 backdrop-blur-md border border-white/15 hover:bg-white/10 hover:border-white/25 transition-all duration-300 cursor-pointer group"
              role="button"
              tabIndex={0}
              aria-label="Open the 5-step guide for new users"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/help');
                }
              }}
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-full bg-white/10 border border-white/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <BookOpen className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-white drop-shadow-sm">
                  New here? Start with the 5-step guide
                </h3>
                <p className="text-sm text-white/70 drop-shadow-sm">
                  Brand → Series → Panel → Configure → Export. Takes about 2 minutes to read.
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all" />
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* Public preview tools — no sign-in needed */}
          <section className="relative z-20 px-4 sm:px-6 -mt-4" aria-labelledby="preview-tools-heading">
            <h2 id="preview-tools-heading" className="sr-only">Public preview tools</h2>
            <div className="container mx-auto max-w-4xl grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => navigate('/compare')}
                className="group text-left p-5 sm:p-6 rounded-xl bg-white/5 backdrop-blur-md border border-white/15 hover:bg-white/10 hover:border-cyan-400/40 transition-all duration-300"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-full bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Columns className="w-6 h-6 text-cyan-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-white">Try Compare</h3>
                    <p className="text-sm text-white/70">Compare LED panels side by side — no sign-in needed.</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all" />
                </div>
              </button>
              <button
                onClick={() => navigate('/pitch-compare')}
                className="group text-left p-5 sm:p-6 rounded-xl bg-white/5 backdrop-blur-md border border-white/15 hover:bg-white/10 hover:border-cyan-400/40 transition-all duration-300"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-full bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Eye className="w-6 h-6 text-cyan-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-white">Try Pitch Compare</h3>
                    <p className="text-sm text-white/70">See how pixel pitches look at any viewing distance.</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all" />
                </div>
              </button>
            </div>
          </section>
          <TiersSection />
        </>
      )}

      {/* Help Choosing / Troubleshoot Section */}
      <section className="relative z-20 py-12 sm:py-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-8 sm:mb-12 drop-shadow-lg">
            Need Help Choosing?
          </h2>
          <div className={`grid grid-cols-1  gap-6`}>
            <button
              type="button"
              onClick={() => navigate('/troubleshooting')}
              className="block w-full text-center p-8 sm:p-10 rounded-2xl bg-white/10 backdrop-blur-xl border border-brand-magenta/30 hover:bg-white/15 hover:border-brand-magenta/50 hover:shadow-lg hover:shadow-brand-magenta/20 transition-all duration-300 cursor-pointer group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-magenta focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-full bg-gradient-to-br from-brand-blue to-brand-magenta flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <LifeBuoy className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4 drop-shadow-sm">
                Troubleshoot a Screen
              </h3>
              <p className="text-base text-white/80 drop-shadow-sm max-w-md mx-auto">
                Pick a symptom and get a ranked list of things to check first. Free, no sign-in.
              </p>
            </button>
          </div>
        </div>
      </section>



      <RequestCatalogSection />
      </main>


      <Footer />
    </div>
  );
};

export default Index;
